import React from 'react';
import { Icon } from '@iconify/react';
import type { CampaignMetadata } from '../../../types';

/**
 * Outreach execution mode for a campaign, derived from the `send_outreach`
 * flag written at creation (true = Auto, false = Manual, null = legacy row).
 * Single source of truth — reused by every campaign-list surface.
 */
export type OutreachMode = 'auto' | 'manual' | 'unknown';

const isTruthyFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes';
  }
  return false;
};

export const resolveOutreachMode = (campaign?: CampaignMetadata | null): OutreachMode => {
  if (!campaign) return 'unknown';

  // 1. Explicit mode string ("manual" | "auto") — the create-flow name.
  const mode = campaign.send_outreach_mode;
  if (typeof mode === 'string' && mode.trim()) {
    const normalized = mode.trim().toLowerCase();
    if (normalized === 'auto') return 'auto';
    if (normalized === 'manual') return 'manual';
  }

  // 2. Boolean flag fallback.
  if (campaign.send_outreach === null || campaign.send_outreach === undefined) {
    // 3. Last resort: block_outreach=false implies outreach is configured —
    //    treat legacy rows with channels but no mode as Manual (the default
    //    at creation when no outreach module was present).
    return 'unknown';
  }
  return isTruthyFlag(campaign.send_outreach) ? 'auto' : 'manual';
};

interface OutreachTypeBadgeProps {
  campaign?: CampaignMetadata | null;
  className?: string;
}

/** Pill with icon: Auto → bot icon, orange. Manual → human icon, blue. */
export const OutreachTypeBadge: React.FC<OutreachTypeBadgeProps> = ({ campaign, className = '' }) => {
  const mode = resolveOutreachMode(campaign);

  if (mode === 'unknown') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white/45 ${className}`}
        title="Outreach mode not recorded for this campaign"
      >
        <Icon icon="bx:help-circle" className="h-3.5 w-3.5" aria-hidden="true" />
        N/A
      </span>
    );
  }

  if (mode === 'auto') {
    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-full border border-orange-400/25 bg-orange-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-orange-200 ${className}`}
        title="Auto — the engine sends outreach automatically"
      >
        <Icon icon="mdi:robot-outline" className="h-3.5 w-3.5" aria-hidden="true" />
        Auto
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-sky-400/25 bg-sky-400/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-sky-200 ${className}`}
      title="Manual — a human reviews and sends the outreach"
    >
      <Icon icon="mdi:account-outline" className="h-3.5 w-3.5" aria-hidden="true" />
      Manual
    </span>
  );
};
